export interface GuidedContent {
  help: string;
  nextStep: string;
  title: string;
}

export interface FeatureRoute {
  administratorsOnly?: boolean;
  description: string;
  guide: GuidedContent;
  label: string;
  path: string;
}

export interface NavigationItem {
  administratorsOnly?: boolean;
  curatorsOnly?: boolean;
  label: string;
  path: string;
}

export interface NavigationGroup {
  items: readonly NavigationItem[];
  label:
    "Projects" | "Equipment" | "Route" | "Calculate" | "Results" | "Settings";
}

export const featureRoutes = [
  {
    path: "/app",
    label: "Dashboard",
    description: "Continue a project.",
    guide: {
      title: "Choose a project",
      nextStep: "Open a recent project or create one.",
      help: "The selected project controls the data shown on each screen.",
    },
  },
  {
    path: "/projects",
    label: "Projects",
    description: "Create or select a project.",
    guide: {
      title: "Select a project",
      nextStep: "Create or select one, then open Network.",
      help: "A project keeps its route, scenarios, results, and exports.",
    },
  },
  {
    path: "/catalog",
    label: "Catalog",
    description: "Add pump and hose models.",
    guide: {
      title: "Add equipment models",
      nextStep: "Add the pump and hose models you can use.",
      help: "Models are reused across projects.",
    },
  },
  {
    path: "/fleet",
    label: "Fleet",
    description: "Add physical pump units.",
    guide: {
      title: "Add pump units",
      nextStep: "Add each physical pump available.",
      help: "A unit can be placed once.",
    },
  },
  {
    path: "/curves",
    label: "Curves",
    description: "Store checked pump curves.",
    guide: {
      title: "Add pump curves",
      nextStep: "Choose a pump, enter its curve, and save it.",
      help: "Calculations use verified curves only.",
    },
  },
  {
    path: "/network",
    label: "Network",
    description: "Build the permitted route.",
    guide: {
      title: "Build the route",
      nextStep:
        "Import the route, then place sources, deliveries, and pump sites.",
      help: "Calculations stay on the imported route.",
    },
  },
  {
    path: "/scenarios",
    label: "Scenarios",
    description: "Set operating requirements.",
    guide: {
      title: "Set requirements",
      nextStep: "Enter source limits and delivery requirements.",
      help: "Required cases share equipment and locations.",
    },
  },
  {
    path: "/simulation",
    label: "Simulation",
    description: "Check one pump plan.",
    guide: {
      title: "Check one plan",
      nextStep: "Choose a scenario and pump plan, then run it.",
      help: "The result checks hydraulic and equipment limits.",
    },
  },
  {
    path: "/optimization",
    label: "Optimization",
    description: "Find a pump plan.",
    guide: {
      title: "Find a pump plan",
      nextStep: "Run Preview. Use Thorough for a minimum proof.",
      help: "Stopped runs report only the best design found.",
    },
  },
  {
    path: "/results",
    label: "Results",
    description: "Review saved results.",
    guide: {
      title: "Review the result",
      nextStep: "Check status, limiting condition, and pump plan.",
      help: "Saved results keep their original inputs.",
    },
  },
  {
    path: "/exports",
    label: "Exports",
    description: "Download reviewed work.",
    guide: {
      title: "Download reviewed work",
      nextStep: "Choose a saved result and file type.",
      help: "Bundles omit credentials and local paths.",
    },
  },
  {
    path: "/settings",
    label: "Settings",
    description: "Check map and terrain setup.",
    guide: {
      title: "Check setup",
      nextStep: "Review map and terrain status.",
      help: "Provider credentials are not exported.",
    },
  },
] as const satisfies readonly FeatureRoute[];

function featureRoute(path: string): FeatureRoute {
  const route = featureRoutes.find((candidate) => candidate.path === path);
  if (route === undefined) {
    throw new Error(`Navigation references unknown feature route: ${path}`);
  }
  return route;
}

export const usersNavigationItem = {
  path: "/users",
  label: "Users",
  administratorsOnly: true,
} as const satisfies NavigationItem;

export const curveReviewNavigationItem = {
  path: "/admin/curve-library",
  label: "Curve review",
  curatorsOnly: true,
} as const satisfies NavigationItem;

/**
 * The authenticated workflow hierarchy is centralized here so desktop rail,
 * mobile drawer, active-state, and authorization tests share one order.
 */
export const navigationGroups = [
  {
    label: "Projects",
    items: [featureRoute("/app"), featureRoute("/projects")],
  },
  {
    label: "Equipment",
    items: [
      featureRoute("/catalog"),
      featureRoute("/fleet"),
      featureRoute("/curves"),
    ],
  },
  {
    label: "Route",
    items: [featureRoute("/network"), featureRoute("/scenarios")],
  },
  {
    label: "Calculate",
    items: [featureRoute("/simulation"), featureRoute("/optimization")],
  },
  {
    label: "Results",
    items: [featureRoute("/results"), featureRoute("/exports")],
  },
  {
    label: "Settings",
    items: [featureRoute("/settings"), usersNavigationItem, curveReviewNavigationItem],
  },
] as const satisfies readonly NavigationGroup[];

export function routeForPath(pathname: string): FeatureRoute | undefined {
  return featureRoutes.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
